import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Dashboard view of the Études index.
// Manages an array of widget descriptors and persists changes:
//   - on a saved view  -> PATCH /juniors/:jid/saved_views/:id (config.widgets)
//   - on a virtual view -> URL param dashboard_widgets=<JSON> + reload
//
// Widget descriptor shape:
//   { id, type, title, metric, group_by?, time_axis?, size, limit? }
export default class extends Controller {
  static targets = [
    "grid", "widget", "picker", "typeOption",
    "metricSelect", "groupBySelect", "groupBySection",
    "timeAxisSelect", "timeAxisSection",
    "titleInput", "sizeSelect", "editToggle", "dragHandle"
  ]

  static values = {
    widgets:     Array,
    catalog:     Object,
    editable:    Boolean,
    persistUrl:  String,
    currentView: String
  }

  connect() {
    this._currentType = "kpi"
    this._refreshTypeUI()

    if (this.editableValue && this.hasGridTarget) {
      this.sortable = Sortable.create(this.gridTarget, {
        animation: 150,
        handle: ".widget__action--drag",
        draggable: ".widget",
        ghostClass: "widget--ghost",
        dragClass:  "widget--dragging",
        onEnd: this._onReorder.bind(this)
      })
    }

    // Close picker on Escape
    this._keydownHandler = (e) => {
      if (e.key === "Escape" && this.hasPickerTarget && !this.pickerTarget.hidden) {
        this.closePicker()
      }
    }
    document.addEventListener("keydown", this._keydownHandler)
  }

  disconnect() {
    this.sortable?.destroy()
    document.removeEventListener("keydown", this._keydownHandler)
  }

  // ---- edit mode ---------------------------------------------------

  toggleEdit() {
    const dash = this.element.querySelector(".dashboard")
    if (!dash) return
    const isEditing = dash.classList.toggle("is-editing")
    // Mirror onto the root so toolbar buttons (siblings of .dashboard) can
    // react via plain CSS — see _dashboard_widgets.scss.
    this.element.classList.toggle("is-dashboard-editing", isEditing)
    if (this.hasEditToggleTarget) {
      this.editToggleTarget.classList.toggle("is-active", isEditing)
    }
  }

  // ---- picker modal ------------------------------------------------

  openPicker() {
    if (!this.editableValue) return
    this.pickerTarget.hidden = false
    if (this.hasTitleInputTarget) {
      this.titleInputTarget.value = ""
      // Defer focus to allow the panel transition
      setTimeout(() => this.titleInputTarget.focus(), 60)
    }
  }

  closePicker() {
    if (this.hasPickerTarget) this.pickerTarget.hidden = true
  }

  selectType(event) {
    this._currentType = event.currentTarget.dataset.typeValue
    this._refreshTypeUI()
  }

  _refreshTypeUI() {
    this.typeOptionTargets.forEach(btn => {
      btn.classList.toggle("is-active", btn.dataset.typeValue === this._currentType)
    })
    const t = this._currentType
    if (this.hasGroupBySectionTarget) {
      this.groupBySectionTarget.hidden = !["chart_donut", "chart_bar", "table_top"].includes(t)
    }
    if (this.hasTimeAxisSectionTarget) {
      this.timeAxisSectionTarget.hidden = t !== "chart_line"
    }
  }

  // ---- mutations ---------------------------------------------------

  addWidget() {
    const t = this._currentType
    const widget = {
      id:     this._newId(),
      type:   t,
      title:  (this.titleInputTarget.value || "").trim() || this._defaultTitle(t),
      metric: this.metricSelectTarget.value,
      size:   parseInt(this.sizeSelectTarget.value, 10) || 1
    }

    if (["chart_donut", "chart_bar", "table_top"].includes(t)) {
      widget.group_by = this.groupBySelectTarget.value
    }
    if (t === "chart_line") {
      widget.time_axis = this.timeAxisSelectTarget.value
    }
    if (t === "table_top") {
      widget.limit = 5
    }

    const widgets = [...this.widgetsValue, widget]
    this._persist(widgets)
  }

  removeWidget(event) {
    const id = event.currentTarget.dataset.widgetId
    if (!id) return
    if (!confirm("Supprimer ce widget ?")) return
    const widgets = this.widgetsValue.filter(w => w.id !== id)
    this._persist(widgets)
  }

  changeSize(event) {
    const id = event.currentTarget.dataset.widgetId
    const newSize = parseInt(event.currentTarget.value, 10) || 1
    const widgets = this.widgetsValue.map(w => w.id === id ? { ...w, size: newSize } : w)
    this._persist(widgets)
  }

  _onReorder() {
    // Read DOM order and rebuild widgets array
    const orderedIds = Array.from(this.gridTarget.querySelectorAll(".widget"))
      .map(el => el.dataset.widgetId)
    const byId = new Map(this.widgetsValue.map(w => [w.id, w]))
    const widgets = orderedIds.map(id => byId.get(id)).filter(Boolean)
    if (widgets.length !== this.widgetsValue.length) return
    this._persist(widgets)
  }

  // ---- persistence -------------------------------------------------

  async _persist(widgets) {
    if (this.persistUrlValue) {
      // On a saved view → PATCH then reload
      const idx = window.__listIndex || window.__etudesIndex
      const config = {
        view_type: "dashboard",
        widgets,
        filters: idx?.filtersValue || {}
      }
      const res = await fetch(this.persistUrlValue, {
        method:  "PATCH",
        headers: this._headers(),
        body:    JSON.stringify({ saved_view: { config: JSON.stringify(config) } })
      })
      if (res.ok) {
        window.location.reload()
      } else {
        const err = await res.json().catch(() => ({}))
        alert(err.errors?.join(", ") || "Erreur lors de la mise à jour de la vue.")
      }
    } else {
      // Virtual view (mine/junior) → bake widgets into URL param and reload
      const url = new URL(window.location.href)
      url.searchParams.set("view_type", "dashboard")
      url.searchParams.set("dashboard_widgets", JSON.stringify(widgets))
      window.location.href = url.toString()
    }
  }

  // ---- helpers -----------------------------------------------------

  _newId() {
    return "w-" + Date.now().toString(36) + "-" + Math.floor(Math.random() * 1e6).toString(36)
  }

  _defaultTitle(type) {
    const cat = this.catalogValue || {}
    const metric = (cat.metrics || []).find(m => m.value === this.metricSelectTarget.value)
    const typeLabel = (cat.widget_types || []).find(t => t.value === type)?.label
    if (type === "kpi") return metric?.label || "Indicateur"
    if (type === "chart_line") {
      const axis = (cat.time_axes || []).find(a => a.value === this.timeAxisSelectTarget.value)?.label
      return [metric?.label, axis].filter(Boolean).join(" — ") || (typeLabel || "Graphique")
    }
    const grp = (cat.group_by || []).find(g => g.value === this.groupBySelectTarget.value)?.label
    return [metric?.label, grp].filter(Boolean).join(" par ") || (typeLabel || "Widget")
  }

  _headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    return {
      "Content-Type": "application/json",
      "Accept":       "application/json",
      "X-CSRF-Token": csrf || ""
    }
  }
}
